export const MAX_INPUT_CHARACTERS = 200_000
export const MAX_INPUT_LINES = 20_000

export type CleanupPreset =
  | 'normalizeSpaces'
  | 'removeEmptyLines'
  | 'joinWrappedLines'
  | 'tabsToSpaces'
  | 'trimLines'

export interface CleanupOptions {
  normalizeSpaces: boolean
  removeEmptyLines: boolean
  joinWrappedLines: boolean
  tabsToSpaces: boolean
  trimLines: boolean
}

export interface TextCounts {
  characters: number
  lines: number
  emptyLines: number
}

export interface CleanupResult {
  output: string
  before: TextCounts
  after: TextCounts
  changed: boolean
  lineEnding: 'lf' | 'crlf' | 'cr'
}

export type CleanupError = 'character_limit' | 'line_limit'

export type CleanupAttempt =
  | { status: 'success'; result: CleanupResult }
  | { status: 'error'; error: CleanupError }

const BLANK_LINE = /^[\t ]*$/

function detectLineEnding(text: string): CleanupResult['lineEnding'] {
  const counts = {
    crlf: text.match(/\r\n/g)?.length ?? 0,
    lf: text.match(/(?<!\r)\n/g)?.length ?? 0,
    cr: text.match(/\r(?!\n)/g)?.length ?? 0,
  }

  const entries: Array<[CleanupResult['lineEnding'], number]> = [
    ['lf', counts.lf],
    ['crlf', counts.crlf],
    ['cr', counts.cr],
  ]
  return entries.reduce((selected, candidate) =>
    candidate[1] > selected[1] ? candidate : selected,
  )[0]
}

function outputLineEnding(kind: CleanupResult['lineEnding']): string {
  if (kind === 'crlf') return '\r\n'
  if (kind === 'cr') return '\r'
  return '\n'
}

function normalizeLineEndings(text: string): string {
  return text.replace(/\r\n?/g, '\n')
}

function countText(text: string): TextCounts {
  if (text.length === 0) {
    return { characters: 0, lines: 0, emptyLines: 0 }
  }

  const lines = normalizeLineEndings(text).split('\n')
  return {
    characters: countCodePoints(text),
    lines: lines.length,
    emptyLines: lines.filter((line) => BLANK_LINE.test(line)).length,
  }
}

function transformLine(line: string, options: CleanupOptions): string {
  let result = line

  if (options.normalizeSpaces) {
    result = result.replace(/ {2,}/g, ' ')
  }
  if (options.tabsToSpaces) {
    result = result.replace(/\t/g, '  ')
  }
  if (options.trimLines) {
    result = result.replace(/^[\t ]+|[\t ]+$/g, '')
  }

  return result
}

function joinParagraphLines(lines: string[], removeEmptyLines: boolean): string[] {
  const output: string[] = []
  let paragraph: string[] = []

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    output.push(paragraph.join(' '))
    paragraph = []
  }

  for (const line of lines) {
    if (BLANK_LINE.test(line)) {
      flushParagraph()
      if (!removeEmptyLines) {
        output.push('')
      }
      continue
    }

    paragraph.push(line)
  }

  flushParagraph()
  return output
}

function countCodePoints(text: string, stopAfter?: number): number {
  if (stopAfter !== undefined && text.length <= stopAfter) {
    return text.length
  }

  let count = 0
  for (let index = 0; index < text.length; index += 1) {
    const current = text.charCodeAt(index)
    if (
      current >= 0xd800
      && current <= 0xdbff
      && index + 1 < text.length
    ) {
      const next = text.charCodeAt(index + 1)
      if (next >= 0xdc00 && next <= 0xdfff) {
        index += 1
      }
    }
    count += 1
    if (stopAfter !== undefined && count > stopAfter) {
      return count
    }
  }
  return count
}

export function cleanText(text: string, options: CleanupOptions): CleanupAttempt {
  if (countCodePoints(text, MAX_INPUT_CHARACTERS) > MAX_INPUT_CHARACTERS) {
    return { status: 'error', error: 'character_limit' }
  }

  const normalized = normalizeLineEndings(text)
  const inputLines = text.length === 0 ? [] : normalized.split('\n')
  if (inputLines.length > MAX_INPUT_LINES) {
    return { status: 'error', error: 'line_limit' }
  }

  const lineEnding = detectLineEnding(text)
  const transformedLines = inputLines.map((line) => transformLine(line, options))
  const outputLines = options.joinWrappedLines
    ? joinParagraphLines(transformedLines, options.removeEmptyLines)
    : options.removeEmptyLines
      ? transformedLines.filter((line) => !BLANK_LINE.test(line))
      : transformedLines
  const output = outputLines.join(outputLineEnding(lineEnding))

  return {
    status: 'success',
    result: {
      output,
      before: countText(text),
      after: countText(output),
      changed: output !== text,
      lineEnding,
    },
  }
}
